import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Loader2,
  Lock,
  LockOpen,
  Plus,
  RefreshCw,
  Save,
  Send,
  Sparkles,
  Trash2,
  Wand2,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { api, type ApiResponse } from "@/api/client";
import SectionFieldsEditor from "@/components/newsletter/SectionFieldsEditor";
import {
  SECTION_LABELS,
  THEME_OPTIONS,
  defaultSection,
  type NewsletterSection,
  type NewsletterSubjectVariant,
  type StudioCampaign,
} from "@/components/newsletter/studioTypes";

/**
 * Edition editor (Řez 6) — the section-based replacement for the legacy raw-HTML campaign
 * editor. Left: live email preview (server-rendered iframe, debounced). Right: section panel
 * with per-section prompt edits (AI rewrite that respects locks), manual fields, reorder,
 * lock toggles, theme/palette switch and subject variants. Legacy HTML campaigns (no
 * sectionsJson) keep opening in the old editor.
 */

interface EditionState {
  subject: string;
  preheader: string;
  themeKey: string;
  subjectVariants: NewsletterSubjectVariant[];
  sections: NewsletterSection[];
}

export default function EditionEditorPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["studio-campaign", id],
    queryFn: () => api.get<ApiResponse<StudioCampaign>>(`/api/admin/newsletter/campaigns/${id}`),
    enabled: !!id,
  });

  const [edition, setEdition] = useState<EditionState | null>(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSize, setPreviewSize] = useState<number | null>(null);
  const [openSection, setOpenSection] = useState<number | null>(null);
  const [promptDrafts, setPromptDrafts] = useState<Record<number, string>>({});
  const [globalInstruction, setGlobalInstruction] = useState("");
  const [rewriting, setRewriting] = useState<number | "global" | null>(null);
  const [showSend, setShowSend] = useState(false);

  // Hydrate editor state from the loaded campaign once.
  useEffect(() => {
    const c = data?.data;
    if (!c || edition) return;
    if (!c.sectionsJson) return; // legacy campaign — redirect below
    setEdition({
      subject: c.subject,
      preheader: c.preheader ?? "",
      themeKey: c.themeKey ?? "violet",
      subjectVariants: c.subjectVariants ?? [],
      sections: c.sectionsJson,
    });
  }, [data, edition]);

  const campaign = data?.data;
  useEffect(() => {
    if (campaign && !campaign.sectionsJson) {
      navigate(`/newsletter/editor/${campaign.id}`, { replace: true });
    }
  }, [campaign, navigate]);

  // ── Live preview (debounced server render) ──────────────────────────────────────────
  const previewTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const refreshPreview = useCallback(
    (ed: EditionState) => {
      clearTimeout(previewTimer.current);
      previewTimer.current = setTimeout(async () => {
        try {
          const res = await api.post<ApiResponse<{ html: string; sizeBytes: number }>>(
            "/api/admin/newsletter-studio/preview",
            {
              subject: ed.subject || t("newsletter.campaignEditor.noSubject"),
              preheader: ed.preheader || ed.subject || "-",
              themeKey: ed.themeKey,
              subjectVariants: ed.subjectVariants,
              sections: ed.sections,
              campaignId: id,
            },
          );
          if (res.data) {
            setPreviewHtml(res.data.html);
            setPreviewSize(res.data.sizeBytes);
          }
        } catch (e) {
          toast.error(e instanceof Error ? e.message : t("newsletter.editionEditor.previewFailed"));
        }
      }, 600);
    },
    [id, t],
  );

  useEffect(() => {
    if (edition) refreshPreview(edition);
  }, [edition, refreshPreview]);

  const update = (patch: Partial<EditionState>) =>
    setEdition((prev) => (prev ? { ...prev, ...patch } : prev));

  const updateSection = (index: number, next: NewsletterSection) => {
    if (!edition) return;
    const sections = [...edition.sections];
    sections[index] = next;
    update({ sections });
  };

  const moveSection = (index: number, dir: -1 | 1) => {
    if (!edition) return;
    const target = index + dir;
    if (target < 0 || target >= edition.sections.length) return;
    const sections = [...edition.sections];
    [sections[index], sections[target]] = [sections[target], sections[index]];
    update({ sections });
    setOpenSection(target);
  };

  const removeSection = (index: number) => {
    if (!edition) return;
    update({ sections: edition.sections.filter((_, i) => i !== index) });
    setOpenSection(null);
  };

  const addSection = (type: string) => {
    if (!edition) return;
    const sections = [...edition.sections];
    const footerIdx = sections.findIndex((s) => s.type === "footer");
    const insertAt = footerIdx >= 0 ? footerIdx : sections.length;
    sections.splice(insertAt, 0, defaultSection(type));
    update({ sections });
    setOpenSection(insertAt);
  };

  // ── AI rewrite (per section / global) ───────────────────────────────────────────────
  const rewrite = async (sectionIndex: number | undefined, instruction: string) => {
    if (!edition || !instruction.trim()) return;
    setRewriting(sectionIndex ?? "global");
    try {
      const res = await api.post<ApiResponse<{ sections: NewsletterSection[] }>>(
        "/api/admin/newsletter-studio/sections/rewrite",
        { sections: edition.sections, sectionIndex, instruction, campaignId: id },
      );
      if (res.data?.sections) {
        update({ sections: res.data.sections });
        toast.success(t("newsletter.editionEditor.rewritten"));
        if (sectionIndex !== undefined) setPromptDrafts((p) => ({ ...p, [sectionIndex]: "" }));
        else setGlobalInstruction("");
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("newsletter.editionEditor.rewriteFailed"));
    } finally {
      setRewriting(null);
    }
  };

  // ── Save ────────────────────────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: (ed: EditionState) =>
      api.put(`/api/admin/newsletter-studio/campaigns/${id}/edition`, {
        subject: ed.subject,
        preheader: ed.preheader,
        themeKey: ed.themeKey,
        subjectVariants: ed.subjectVariants,
        sections: ed.sections,
      }),
    onSuccess: () => toast.success(t("newsletter.editionEditor.saved")),
    onError: (e: Error) => toast.error(e.message || t("newsletter.editionEditor.saveFailed")),
  });

  if (isLoading || (campaign && !campaign.sectionsJson)) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="text-primary-600 h-8 w-8 animate-spin" />
      </div>
    );
  }
  if (!campaign || !edition) {
    return (
      <div className="card text-center text-gray-500">{t("newsletter.editionEditor.notFound")}</div>
    );
  }

  const sizeKb = previewSize !== null ? Math.round(previewSize / 102.4) / 10 : null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/newsletter-studio")} className="btn-secondary !px-3">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {t("newsletter.editionEditor.title")}
            </h1>
            <p className="text-xs text-gray-500">
              {campaign.status === "generated" ? t("newsletter.editionEditor.aiDraftPrefix") : ""}
              {campaign.aiPrompt ?? ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {sizeKb !== null && (
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-medium ${sizeKb > 100 ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}
            >
              {sizeKb} kB
            </span>
          )}
          <select
            value={edition.themeKey}
            onChange={(e) => update({ themeKey: e.target.value })}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
          >
            {THEME_OPTIONS.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </select>
          <button
            onClick={() =>
              rewrite(
                undefined,
                "Regenerate the copy with a fresh angle while keeping the same structure, trips and theme.",
              )
            }
            disabled={rewriting !== null}
            className="btn-secondary flex items-center gap-1.5"
            title={t("newsletter.editionEditor.regenerateTitle")}
          >
            <RefreshCw className={`h-4 w-4 ${rewriting === "global" ? "animate-spin" : ""}`} />
            {t("newsletter.editionEditor.regenerate")}
          </button>
          <button
            onClick={() => saveMutation.mutate(edition)}
            disabled={saveMutation.isPending}
            className="btn-primary flex items-center gap-1.5"
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {t("common.actions.save")}
          </button>
          {campaign.status !== "sent" && campaign.status !== "sending" && (
            <button
              onClick={async () => {
                await saveMutation.mutateAsync(edition);
                setShowSend(true);
              }}
              className="flex items-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
            >
              <Send className="h-4 w-4" />
              {t("newsletter.editionEditor.sendEllipsis")}
            </button>
          )}
        </div>
      </div>

      {showSend && id && (
        <SendDialog
          campaignId={id}
          generationRunId={campaign?.generationRunId ?? null}
          variantCount={edition?.subjectVariants.length ?? 0}
          onClose={() => setShowSend(false)}
        />
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Preview */}
        <div className="card sticky top-4 h-[calc(100vh-140px)] overflow-hidden !p-0">
          {previewHtml ? (
            <iframe
              title={t("newsletter.editionEditor.previewIframeTitle")}
              srcDoc={previewHtml}
              className="h-full w-full border-0"
              sandbox=""
            />
          ) : (
            <div className="flex h-full items-center justify-center text-gray-400">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />{" "}
              {t("newsletter.editionEditor.renderingPreview")}
            </div>
          )}
        </div>

        {/* Section panel */}
        <div className="space-y-3">
          {/* Subject + preheader + variants */}
          <div className="card space-y-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
                {t("newsletter.editionEditor.subject")}
              </span>
              <input
                type="text"
                value={edition.subject}
                onChange={(e) => update({ subject: e.target.value })}
                className="focus:ring-primary-500 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium focus:outline-none focus:ring-1"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
                {t("newsletter.editionEditor.preheader")}
              </span>
              <input
                type="text"
                value={edition.preheader}
                onChange={(e) => update({ preheader: e.target.value })}
                className="focus:ring-primary-500 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1"
              />
            </label>
            {edition.subjectVariants.length > 0 && (
              <div>
                <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
                  {t("newsletter.editionEditor.abVariants")}
                </span>
                <div className="space-y-1.5">
                  {edition.subjectVariants.map((v, i) => (
                    <div key={i} className="flex gap-2">
                      <input
                        type="text"
                        value={v.subject}
                        onChange={(e) => {
                          const subjectVariants = [...edition.subjectVariants];
                          subjectVariants[i] = { ...v, subject: e.target.value };
                          update({ subjectVariants });
                        }}
                        className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
                      />
                      <input
                        type="text"
                        value={v.preheader}
                        onChange={(e) => {
                          const subjectVariants = [...edition.subjectVariants];
                          subjectVariants[i] = { ...v, preheader: e.target.value };
                          update({ subjectVariants });
                        }}
                        className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Global instruction */}
          <div className="card flex items-center gap-2 !py-3">
            <Sparkles className="text-primary-600 h-4 w-4 shrink-0" />
            <input
              type="text"
              value={globalInstruction}
              onChange={(e) => setGlobalInstruction(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && rewrite(undefined, globalInstruction)}
              placeholder={t("newsletter.editionEditor.globalInstructionPlaceholder")}
              className="flex-1 border-0 text-sm focus:outline-none"
            />
            <button
              onClick={() => rewrite(undefined, globalInstruction)}
              disabled={rewriting !== null || !globalInstruction.trim()}
              className="btn-secondary !px-3 !py-1.5"
            >
              {rewriting === "global" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="h-4 w-4" />
              )}
            </button>
          </div>

          {/* Sections */}
          {edition.sections.map((section, i) => (
            <div key={i} className="card !p-0">
              <div
                className="flex cursor-pointer items-center gap-2 px-4 py-2.5"
                onClick={() => setOpenSection(openSection === i ? null : i)}
              >
                <span className="text-sm font-semibold text-gray-800">
                  {SECTION_LABELS[section.type] ?? section.type}
                </span>
                {section.locked && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-700">
                    {t("newsletter.editionEditor.lockedChip")}
                  </span>
                )}
                <span className="flex-1" />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    updateSection(i, { ...section, locked: !section.locked });
                  }}
                  className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                  title={
                    section.locked
                      ? t("newsletter.editionEditor.unlockTitle")
                      : t("newsletter.editionEditor.lockTitle")
                  }
                >
                  {section.locked ? <Lock className="h-4 w-4" /> : <LockOpen className="h-4 w-4" />}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    moveSection(i, -1);
                  }}
                  className="rounded p-1 text-gray-400 hover:bg-gray-100"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    moveSection(i, 1);
                  }}
                  className="rounded p-1 text-gray-400 hover:bg-gray-100"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
                <button
                  title={t("common.actions.remove")}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSection(i);
                  }}
                  className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              {openSection === i && (
                <div className="space-y-3 border-t border-gray-100 px-4 py-3">
                  {!section.locked && section.type !== "footer" && (
                    <div className="flex items-center gap-2 rounded-lg bg-violet-50 px-3 py-2">
                      <Sparkles className="h-4 w-4 shrink-0 text-violet-600" />
                      <input
                        type="text"
                        value={promptDrafts[i] ?? ""}
                        onChange={(e) => setPromptDrafts((p) => ({ ...p, [i]: e.target.value }))}
                        onKeyDown={(e) => e.key === "Enter" && rewrite(i, promptDrafts[i] ?? "")}
                        placeholder={t("newsletter.editionEditor.sectionPromptPlaceholder")}
                        className="flex-1 border-0 bg-transparent text-sm focus:outline-none"
                      />
                      <button
                        onClick={() => rewrite(i, promptDrafts[i] ?? "")}
                        disabled={rewriting !== null || !(promptDrafts[i] ?? "").trim()}
                        className="rounded bg-violet-600 p-1.5 text-white disabled:opacity-40"
                      >
                        {rewriting === i ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Wand2 className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                  )}
                  <SectionFieldsEditor
                    value={section}
                    onChange={(next) => updateSection(i, next)}
                  />
                </div>
              )}
            </div>
          ))}

          {/* Add section */}
          <AddSectionMenu existing={edition.sections} onAdd={addSection} />
        </div>
      </div>
    </div>
  );
}

function AddSectionMenu({
  existing,
  onAdd,
}: {
  existing: NewsletterSection[];
  onAdd: (type: string) => void;
}) {
  const used = useMemo(() => new Set(existing.map((s) => s.type)), [existing]);
  return (
    <div className="card !py-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <Plus className="h-4 w-4 text-gray-400" />
        {Object.entries(SECTION_LABELS)
          .filter(([type]) => type === "note" || !used.has(type as NewsletterSection["type"]))
          .map(([type, label]) => (
            <button
              key={type}
              onClick={() => onAdd(type)}
              className="rounded-full border border-gray-200 px-2.5 py-1 text-xs text-gray-600 hover:border-violet-300 hover:text-violet-700"
            >
              {label}
            </button>
          ))}
      </div>
    </div>
  );
}

interface AudiencePreview {
  eligible: number;
  suppressed: number;
  capped: number;
  /** Recipient's own pacing choice (preference center): snoozed or monthly-too-soon. */
  paused?: number;
  dormant: number;
  byBasis: Record<string, number>;
  testWave: boolean;
  // Optional: deploy-skew guard — a cached pre-deploy preview may lack it (copy falls back to ~3).
  wave2DelayHours?: number;
  segments: Array<{ id: string; name: string; customerCount: number }>;
}

/** Send flow (Řez 7): audience breakdown, segment narrowing, send-test, schedule, two-wave send. */
function SendDialog({
  campaignId,
  generationRunId,
  variantCount,
  onClose,
}: {
  campaignId: string;
  generationRunId: string | null;
  /** In the query key so an edited variant list invalidates the cached testWave preview. */
  variantCount: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [segmentId, setSegmentId] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [scheduledFor, setScheduledFor] = useState("");
  const [whyThisDraft, setWhyThisDraft] = useState("");
  const [busy, setBusy] = useState<"test" | "send" | null>(null);

  const { data } = useQuery({
    queryKey: ["audience-preview", campaignId, segmentId, variantCount],
    queryFn: () =>
      // campaignId lets the API factor the campaign's subject-variant count into testWave —
      // without it the dialog could promise an A/B wave that dispatch then skips.
      api.get<ApiResponse<AudiencePreview>>(
        `/api/admin/newsletter-studio/audience-preview?campaignId=${campaignId}${segmentId ? `&segmentId=${segmentId}` : ""}`,
      ),
  });
  const preview = data?.data;

  const sendTest = async () => {
    setBusy("test");
    try {
      await api.post(`/api/admin/newsletter/campaigns/${campaignId}/send-test`, {
        email: testEmail.trim() || undefined,
      });
      toast.success(t("newsletter.sendDialog.testSentToast"));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("newsletter.sendDialog.testFailed"));
    } finally {
      setBusy(null);
    }
  };

  const sendNow = async () => {
    if (
      !window.confirm(
        scheduledFor
          ? t("newsletter.sendDialog.confirmSchedule", { time: scheduledFor })
          : t("newsletter.sendDialog.confirmSendNow", { n: preview?.eligible ?? "?" }),
      )
    ) {
      return;
    }
    setBusy("send");
    try {
      const res = await api.post<{ message?: string }>(
        `/api/admin/newsletter/campaigns/${campaignId}/send`,
        {
          segmentId: segmentId || undefined,
          // datetime-local is the admin's LOCAL wall clock — convert to a UTC instant here,
          // because the dispatcher cron compares against UTC now.
          scheduledFor: scheduledFor ? new Date(scheduledFor).toISOString() : undefined,
        },
      );
      // The act of sending IS choosing this draft over its siblings — file the optional
      // "why" into the learning loop now (fire-and-forget, never blocks the send).
      if (whyThisDraft.trim() && generationRunId) {
        api
          .post("/api/admin/newsletter-studio/feedback", {
            scope: "draft",
            refId: campaignId,
            note: whyThisDraft.trim(),
            meta: { runId: generationRunId },
          })
          .catch(() => undefined);
      }
      toast.success(res.message ?? t("newsletter.shared.sendingToast"));
      onClose();
      navigate("/newsletter-studio");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("newsletter.sendDialog.sendFailed"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6">
      <div className="w-full max-w-lg space-y-4 rounded-xl bg-white p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {t("newsletter.sendDialog.title")}
          </h3>
          <button
            title={t("common.actions.close")}
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {preview ? (
          <div className="grid grid-cols-5 gap-2 text-center">
            <div className="rounded-lg bg-green-50 px-2 py-3">
              <p className="text-xl font-bold text-green-700">{preview.eligible}</p>
              <p className="text-[11px] text-green-700">{t("newsletter.sendDialog.eligible")}</p>
            </div>
            <div className="rounded-lg bg-gray-50 px-2 py-3">
              <p className="text-xl font-bold text-gray-600">{preview.suppressed}</p>
              <p className="text-[11px] text-gray-500">{t("newsletter.sendDialog.unsubscribed")}</p>
            </div>
            <div className="rounded-lg bg-amber-50 px-2 py-3">
              <p className="text-xl font-bold text-amber-700">{preview.capped}</p>
              <p className="text-[11px] text-amber-700">{t("newsletter.sendDialog.freqCapped")}</p>
            </div>
            <div className="rounded-lg bg-sky-50 px-2 py-3">
              <p className="text-xl font-bold text-sky-700">{preview.paused ?? 0}</p>
              <p className="text-[11px] text-sky-700">{t("newsletter.sendDialog.paused")}</p>
            </div>
            <div className="rounded-lg bg-gray-50 px-2 py-3">
              <p className="text-xl font-bold text-gray-600">{preview.dormant}</p>
              <p className="text-[11px] text-gray-500">{t("newsletter.sendDialog.dormant")}</p>
            </div>
          </div>
        ) : (
          <Loader2 className="mx-auto h-5 w-5 animate-spin text-gray-300" />
        )}
        {preview && (
          <p className="text-xs text-gray-500">
            {t("newsletter.sendDialog.basis", {
              explicit: preview.byBasis.explicit ?? 0,
              customer: preview.byBasis.customer ?? 0,
              lead: preview.byBasis.lead ?? 0,
            })}{" "}
            {preview.testWave
              ? t("newsletter.sendDialog.abTestInfo", { hours: preview.wave2DelayHours ?? 3 })
              : t("newsletter.sendDialog.singleWave")}
          </p>
        )}

        <label className="block text-sm">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
            {t("newsletter.sendDialog.narrowToSegment")}
          </span>
          <select
            value={segmentId}
            onChange={(e) => setSegmentId(e.target.value)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
          >
            <option value="">{t("newsletter.sendDialog.everyoneEligible")}</option>
            {(preview?.segments ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.customerCount})
              </option>
            ))}
          </select>
        </label>

        <div className="flex items-end gap-2 rounded-lg bg-gray-50 p-3">
          <label className="block flex-1 text-sm">
            <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
              {t("newsletter.sendDialog.sendTestTo")}
            </span>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder={t("newsletter.sendDialog.testEmailPlaceholder")}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
          </label>
          <button onClick={sendTest} disabled={busy !== null} className="btn-secondary">
            {busy === "test" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              t("newsletter.campaignEditor.sendTest")
            )}
          </button>
        </div>

        <label className="block text-sm">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
            {t("newsletter.sendDialog.scheduleOptional")}
          </span>
          <input
            type="datetime-local"
            value={scheduledFor}
            onChange={(e) => setScheduledFor(e.target.value)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
        </label>

        {generationRunId && (
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
              {t("newsletter.sendDialog.whyThisDraft")}
            </span>
            <input
              type="text"
              value={whyThisDraft}
              onChange={(e) => setWhyThisDraft(e.target.value)}
              placeholder={t("newsletter.sendDialog.whyPlaceholder")}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
          </label>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary">
            {t("common.actions.cancel")}
          </button>
          <button
            onClick={sendNow}
            disabled={busy !== null || !preview || preview.eligible === 0}
            className="flex items-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            {busy === "send" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {scheduledFor
              ? t("newsletter.campaignEditor.schedule")
              : t("newsletter.sendDialog.sendNow")}
          </button>
        </div>
      </div>
    </div>
  );
}
